import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Rss, Globe, RefreshCw, Trash2, Plus, AlertTriangle, CheckCircle2, Gavel, Shield, Scale, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

// -------- Eligibility styling map --------
const ELIGIBILITY_STYLE = {
  eligible: { label: "Eligible", cls: "bg-forest/10 text-forest border-forest/40" },
  eligible_via_fiscal_sponsor: { label: "Eligible · Fractured Atlas", cls: "bg-forest/10 text-forest border-forest/40" },
  eligible_as_individual: { label: "Eligible · Founder", cls: "bg-forest/10 text-forest border-forest/40" },
  eligible_via_partner: { label: "Eligible · Partner", cls: "bg-forest/10 text-forest border-forest/40" },
  eligible_via_consortium: { label: "Eligible · Consortium", cls: "bg-forest/10 text-forest border-forest/40" },
  future_eligible: { label: "Future-eligible", cls: "bg-terracotta/10 text-terracotta border-terracotta/40" },
  requires_legal_review: { label: "Legal review required", cls: "bg-terracotta/10 text-terracotta border-terracotta/40" },
  needs_verification: { label: "Needs verification", cls: "bg-terracotta/10 text-terracotta border-terracotta/40" },
  unknown: { label: "Unknown", cls: "bg-bone text-ink-soft border-line" },
  ineligible: { label: "Ineligible", cls: "bg-line text-ink-soft border-line line-through decoration-1" },
};

// -------- Verdict tier styling --------
const VERDICT_STYLE = {
  GO:     { label: "GO",     cls: "bg-forest/10 text-forest border-forest/40" },
  REVIEW: { label: "REVIEW", cls: "bg-terracotta/10 text-terracotta border-terracotta/40" },
  NO_GO:  { label: "NO_GO",  cls: "bg-ink/10 text-ink border-ink/30" },
};

// -------- Filtered-reason human-readable map --------
// Canonical values are stored on the row; label them in Founder language.
// Missing entries fall through to the raw stored value in title case.
const FILTER_REASON_LABEL = {
  low_institutional_relevance: "Low institutional relevance",
  excluded_by_type:            "Excluded by type",
  applicant_type_restricted:   "Applicant type restricted",
  geographic_hard_barrier:     "Geographic ineligibility",
  expired:                     "Deadline expired",
  stale_forecast:              "Stale forecast",
};

function humanFilteredReason(reason) {
  if (!reason) return null;
  return FILTER_REASON_LABEL[reason]
    || String(reason).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

const DECISION_OPTIONS = [
  { value: "pursue", label: "Pursue — begin drafting" },
  { value: "defer",  label: "Defer — revisit later" },
  { value: "reject", label: "Reject — save institutional memory" },
  { value: "escalate_legal", label: "Escalate to legal review" },
  { value: "override_eligibility", label: "Override eligibility (advanced)" },
  { value: "note",   label: "Add note only" },
];

function FounderDecisionDialog({ open, onClose, opportunity, onSaved }) {
  const navigate = useNavigate();
  const [decision, setDecision] = useState("pursue");
  const [reason, setReason] = useState("");
  const [rejectionReasonId, setRejectionReasonId] = useState("");
  const [pathway, setPathway] = useState("A");
  const [state, setState] = useState("eligible_via_fiscal_sponsor");
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDecision("pursue"); setReason(""); setRejectionReasonId("");
    setPathway(opportunity?.application_pathway || "A");
    setState(opportunity?.eligibility_state || "eligible_via_fiscal_sponsor");
    api.get("/discovery/rejection-reasons").then(r => setReasons(r.data?.items || [])).catch(() => {});
  }, [open, opportunity]);

  const submit = async () => {
    if (!opportunity?.id) return;
    setSaving(true);
    try {
      const body = { decision, reason };
      if (decision === "reject" && rejectionReasonId) body.rejection_reason_id = rejectionReasonId;
      if (decision === "override_eligibility") { body.pathway = pathway; body.state = state; }
      const { data } = await api.post(`/discovery/opportunities/${opportunity.id}/founder-decision`, body);
      toast.success(`Decision recorded: ${decision}`);
      onSaved?.();
      onClose();
      // Sprint A — Close the Loop. On Pursue, jump straight to the Grant
      // Writer workspace on the newly-linked grant. Founder already decided.
      if (decision === "pursue" && data?.grant_id && data?.grant_writer_staff_id) {
        navigate(`/admin/staff/${data.grant_writer_staff_id}/workspace?grant_id=${data.grant_id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="founder-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Founder Decision</DialogTitle>
          <DialogDescription className="text-xs text-ink-soft italic">
            {opportunity?.title}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-ink-soft">Decision</label>
            <select
              value={decision}
              onChange={e => setDecision(e.target.value)}
              data-testid="decision-select"
              className="w-full h-10 mt-1 border border-line rounded-md bg-bone px-3 text-sm"
            >
              {DECISION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-ink-soft">Reason</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              data-testid="decision-reason"
              placeholder="Institutional memory — future you will thank you."
              className="mt-1 border-line bg-bone"
            />
          </div>
          {decision === "reject" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft">Rejection Reason (canonical)</label>
              <select
                value={rejectionReasonId}
                onChange={e => setRejectionReasonId(e.target.value)}
                data-testid="rejection-reason-select"
                className="w-full h-10 mt-1 border border-line rounded-md bg-bone px-3 text-sm"
              >
                <option value="">— none —</option>
                {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
          )}
          {decision === "override_eligibility" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft">Pathway</label>
                <select value={pathway} onChange={e => setPathway(e.target.value)} data-testid="override-pathway" className="w-full h-10 mt-1 border border-line rounded-md bg-bone px-3 text-sm">
                  {["A","F","D","E","B","C"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft">State</label>
                <select value={state} onChange={e => setState(e.target.value)} data-testid="override-state" className="w-full h-10 mt-1 border border-line rounded-md bg-bone px-3 text-sm">
                  {Object.keys(ELIGIBILITY_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="decision-cancel" className="border-line">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="decision-submit" className="bg-forest text-bone hover:bg-ink rounded-full">
            {saving ? "Saving…" : "Save decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Discovery() {
  const [sources, setSources] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [summary, setSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedOp, setExpandedOp] = useState(null);
  const [rescoring, setRescoring] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [decisionOp, setDecisionOp] = useState(null);
  const [form, setForm] = useState({ kind: "html", label: "", url: "", funder: "" });
  // 2026-02-09 · Read-path reconciliation. The Founder view retrieves the
  // FULL qualified population (include_filtered=true) and organizes it into
  // named tabs (VISIBLE / GO / REVIEW / NO_GO / FILTERED / ALL). Each tab
  // count is derived from the same population as the rows shown below.
  const [tab, setTab] = useState("VISIBLE");

  const load = useCallback(async () => {
    try {
      const [s, w, sum, ops] = await Promise.all([
        api.get("/discovery/sources"),
        api.get("/discovery/watchlist"),
        api.get("/discovery/opportunities/summary"),
        api.get("/discovery/opportunities", { params: { limit: 500, include_filtered: true } }),
      ]);
      setSources(s.data);
      setWatchlist(w.data);
      setSummary(sum.data);
      const sortedOps = (ops.data || []).sort((a, b) =>
        (b.composite_score ?? b.fit_score ?? 0) - (a.composite_score ?? a.fit_score ?? 0)
      );
      setOpportunities(sortedOps);
    } catch (e) { console.debug("discovery admin: load failed", e?.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rescoreAll = async () => {
    setRescoring(true);
    try {
      const { data } = await api.post("/discovery/rescore-all", {});
      toast.success(`Rescored ${data.rescored} opportunities · ${data.failed} failed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRescoring(false); }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/discovery/refresh", {});
      toast.success(`Polled ${data.polled} sources.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const refreshOne = async (source_id) => {
    try {
      await api.post("/discovery/refresh", { source_id });
      toast.success(`Refreshed ${source_id}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addWatch = async () => {
    if (!form.label || !form.url) return toast.error("Label and URL required.");
    try {
      await api.post("/discovery/watchlist", form);
      toast.success(`Added ${form.label} to the watchlist.`);
      setForm({ kind: "html", label: "", url: "", funder: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeWatch = async (id) => {
    if (!window.confirm("Remove this source from the watchlist?")) return;
    try {
      await api.delete(`/discovery/watchlist/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renderSubComposites = (subs) => {
    if (!subs || !Object.keys(subs).length) return null;
    const icons = { quality: Shield, readiness: Clock, strategic: Scale };
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="sub-composites">
        {Object.entries(subs).map(([name, v]) => {
          const Icon = icons[name] || Shield;
          const s = Math.round(v?.score || 0);
          const tone = s >= 70 ? "bg-forest/10 text-forest border-forest/30"
                    : s >= 40 ? "bg-terracotta/10 text-terracotta border-terracotta/30"
                    : "bg-bone text-ink-soft border-line";
          return (
            <span key={name} className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone}`}
                  data-testid={`sub-${name}`}>
              <Icon className="h-3 w-3" strokeWidth={1.5} />
              {name} {s}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="discovery-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Shared Institutional Infrastructure</div>
          <h1 className="font-serif text-4xl mt-1">Discovery Engine</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-2xl leading-relaxed">
            Deterministic eligibility gatekeeper + application-pathway optimizer. Zero recurring LLM cost. Every future AI Staff member consumes this same engine — shared institutional infrastructure, not Grant-Writer-specific.
          </p>
        </div>
        <Button onClick={refreshAll} disabled={refreshing} data-testid="refresh-all" className="bg-forest text-bone hover:bg-ink rounded-full">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
          {refreshing ? "Polling all…" : "Refresh all sources"}
        </Button>
      </div>

      {summary && (
        <div className="rounded-xl border border-terracotta/40 bg-terracotta/5 p-4 flex items-start gap-3" data-testid="coverage-disclaimer">
          <AlertTriangle className="h-4 w-4 text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
          <div className="text-sm text-ink leading-relaxed">
            <div className="font-medium mb-1">Coverage: <span className="text-terracotta">{summary.coverage_label}</span></div>
            <div className="text-xs text-ink-soft italic">{summary.coverage_disclaimer}</div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-2xl text-ink">Ranked Opportunities</h2>
          <Button size="sm" onClick={rescoreAll} disabled={rescoring} data-testid="rescore-all-btn" className="bg-forest text-bone hover:bg-ink rounded-full">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${rescoring ? "animate-spin" : ""}`} strokeWidth={1.5} /> Recompute scores
          </Button>
        </div>
        <p className="text-xs text-ink-soft mb-3 max-w-3xl">
          v2.1 · Deterministic eligibility gate → composite score → founder decision. Click any row to see the 7-factor breakdown, application pathway, and blocked pathways.
        </p>

        {/* 2026-02-09 · Read-path reconciliation · Founder-visible population tabs.
            Each tab shows a distinct subset drawn from the same underlying
            qualified population; every count reflects the exact records the
            list below is rendering. */}
        {(() => {
          const isFiltered = (op) => !!op.filtered_reason;
          const groups = {
            VISIBLE:  opportunities.filter(op => !isFiltered(op)),
            GO:       opportunities.filter(op => op.verdict === "GO"),
            REVIEW:   opportunities.filter(op => op.verdict === "REVIEW"),
            NO_GO:    opportunities.filter(op => op.verdict === "NO_GO"),
            FILTERED: opportunities.filter(op => isFiltered(op)),
            ALL:      opportunities,
          };
          const tabDef = [
            { key: "VISIBLE",  label: "Visible",  hint: "Not filtered · default list" },
            { key: "GO",       label: "GO",       hint: "Sufficiently verified" },
            { key: "REVIEW",   label: "REVIEW",   hint: "Unresolved · human review" },
            { key: "NO_GO",    label: "NO_GO",    hint: "Not presently pursuable" },
            { key: "FILTERED", label: "Filtered", hint: "Has filtered_reason" },
            { key: "ALL",      label: "All",      hint: "Complete population" },
          ];
          const current = groups[tab] || groups.VISIBLE;
          return (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="discovery-population-tabs">
                {tabDef.map(({ key, label, hint }) => {
                  const active = tab === key;
                  const count = groups[key]?.length ?? 0;
                  return (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      title={hint}
                      data-testid={`discovery-tab-${key.toLowerCase()}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-widest transition-colors ${
                        active
                          ? "bg-forest text-bone border-forest"
                          : "bg-bone text-ink border-line hover:border-forest hover:text-forest"
                      }`}
                    >
                      <span>{label}</span>
                      <span className={`inline-flex items-center justify-center min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-[10px] font-mono ${
                        active ? "bg-bone/20 text-bone" : "bg-ink/5 text-ink-soft"
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-ink-soft mb-2" data-testid="discovery-population-legend">
                Showing <span className="font-medium text-ink" data-testid="discovery-visible-count">{current.length}</span> of {opportunities.length} qualified records ·
                {" "}definition: <span className="italic">{tabDef.find(t => t.key === tab)?.hint}</span>
                {summary && (
                  <> · <span title="Full qualified population from /summary">Server totals — GO {summary.verdict_go ?? 0} · REVIEW {summary.verdict_review ?? 0} · NO_GO {summary.verdict_no_go ?? 0} · Filtered {summary.filtered ?? 0} · Visible {summary.visible_default ?? 0}</span></>
                )}
              </div>

        <ul className="space-y-2 mb-6" data-testid="ranked-opportunities">
          {current.length === 0 && (
            <li className="text-sm text-ink-soft italic text-center py-6 border border-dashed border-line rounded-xl">
              No opportunities in this view.
            </li>
          )}
          {current.map(op => {
            const score = op.composite_score ?? op.fit_score ?? 0;
            const bucket = op.priority_bucket || "low";
            const bucketColor = bucket === "high" ? "text-forest border-forest/40 bg-forest/10"
              : bucket === "medium" ? "text-terracotta border-terracotta/40 bg-terracotta/10"
              : "text-ink-soft border-line bg-bone";
            const isOpen = expandedOp === op.id;
            const breakdown = op.score_breakdown || {};
            const elig = ELIGIBILITY_STYLE[op.eligibility_state] || ELIGIBILITY_STYLE.unknown;
            const verdictStyle = op.verdict ? VERDICT_STYLE[op.verdict] : null;
            const filteredLabel = humanFilteredReason(op.filtered_reason);

            return (
              <li key={op.id} className="border border-line bg-admin-surface rounded-xl overflow-hidden" data-testid={`op-${op.id}`}>
                <button
                  onClick={() => setExpandedOp(isOpen ? null : op.id)}
                  className="w-full p-3 flex items-start gap-3 text-left hover:bg-bone transition-colors"
                >
                  <div className={`w-14 h-14 rounded-lg border flex flex-col items-center justify-center shrink-0 ${bucketColor}`}>
                    <div className="text-lg font-serif leading-none">{Math.round(score)}</div>
                    <div className="text-[9px] uppercase tracking-widest mt-0.5">{bucket}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{op.title || "(untitled)"}</div>
                    <div className="text-xs text-ink-soft mt-0.5 flex items-center gap-2 flex-wrap">
                      {op.funder && <span>{op.funder}</span>}
                      {op.deadline && <><span>·</span><span>Due {op.deadline}</span></>}
                      {op.amount_max && <><span>·</span><span>Up to ${Number(op.amount_max).toLocaleString()}</span></>}
                      {op.is_fragile && <Badge className="bg-terracotta/20 text-terracotta border border-terracotta/40 text-[9px] uppercase">Fragile</Badge>}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {verdictStyle && (
                        <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-medium ${verdictStyle.cls}`}
                              data-testid={`verdict-${op.id}`}>
                          {verdictStyle.label}
                        </span>
                      )}
                      {op.eligibility_state && (
                        <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${elig.cls}`}
                              data-testid={`elig-${op.id}`}>
                          {elig.label}
                        </span>
                      )}
                      {filteredLabel && (
                        <span className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest bg-line text-ink-soft border-line"
                              data-testid={`filtered-${op.id}`}
                              title={`filtered_reason=${op.filtered_reason}`}>
                          Filtered · {filteredLabel}
                        </span>
                      )}
                      {op.law_before_math && (
                        <span className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest bg-ink/5 text-ink border-ink/20"
                              data-testid={`lbm-${op.id}`}
                              title="Law-Before-Math short-circuit fired (composite=0)">
                          Law-Before-Math
                        </span>
                      )}
                      {op.recommended_pathway_label && (
                        <span className="text-[11px] text-ink-soft italic truncate">
                          → {op.recommended_pathway_label}
                        </span>
                      )}
                      {op.eligibility_gate_applied && (
                        <Badge className="bg-terracotta/20 text-terracotta border border-terracotta/40 text-[9px] uppercase" title={op.eligibility_gate_reason}>
                          Gate applied
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2">
                      {renderSubComposites(op.sub_composites)}
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-line px-3 py-3 bg-bone space-y-4" data-testid={`breakdown-${op.id}`}>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">7-factor breakdown</div>
                      <div className="grid gap-1.5">
                        {Object.entries(breakdown).map(([key, v]) => (
                          <div key={key} className="grid grid-cols-[1fr,auto,3fr] gap-2 text-xs items-center">
                            <span className="text-ink capitalize">{key.replace(/_/g, " ")}</span>
                            <span className="font-mono text-ink-soft w-16 text-right">
                              {Math.round(v.score)} × {Math.round(v.weight * 100)}%
                            </span>
                            <span className="text-ink-soft italic truncate">{v.explanation}</span>
                          </div>
                        ))}
                        {Object.keys(breakdown).length === 0 && (
                          <div className="text-xs text-ink-soft italic">No breakdown available — run &ldquo;Recompute scores&rdquo; above.</div>
                        )}
                      </div>
                    </div>

                    {op.alternative_pathways?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Alternative pathways</div>
                        <ul className="text-xs text-ink space-y-0.5">
                          {op.alternative_pathways.map((p, i) => (
                            <li key={i} className="text-ink-soft">· {p.label} <span className="text-[10px] italic">({p.state})</span></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {op.blocked_pathways?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Blocked pathways</div>
                        <ul className="text-xs text-ink-soft space-y-0.5">
                          {op.blocked_pathways.map((p, i) => (
                            <li key={i} className="line-through decoration-1">· {p.label} <span className="italic no-underline">— {p.reason}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {op.legal_review_triggers?.length > 0 && (
                      <div className="rounded-lg border border-terracotta/40 bg-terracotta/5 p-2 text-xs text-terracotta">
                        <div className="font-medium mb-0.5">Legal review triggers</div>
                        <div>{op.legal_review_triggers.join(", ")}</div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <Button size="sm" onClick={() => setDecisionOp(op)} data-testid={`decision-btn-${op.id}`} className="bg-forest text-bone hover:bg-ink rounded-full">
                        <Gavel className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Founder Decision
                      </Button>
                      {op.founder_decision && (
                        <Badge className="bg-forest/10 text-forest border border-forest/40 text-[10px] uppercase" data-testid={`decision-tag-${op.id}`}>
                          Decision: {op.founder_decision}
                        </Badge>
                      )}
                      {op.model_version && (
                        <Badge className="bg-bone text-ink-soft border border-line text-[9px]">{op.model_version}</Badge>
                      )}
                      {op.updated_at && (
                        <span className="text-[10px] text-ink-soft" data-testid={`last-eval-${op.id}`} title="Last evaluated (updated_at)">
                          Last evaluated {new Date(op.updated_at).toLocaleDateString()}
                        </span>
                      )}
                      {op.url && (
                        <a href={op.url} target="_blank" rel="noopener noreferrer" className="text-xs text-forest underline ml-auto">
                          Open opportunity →
                        </a>
                      )}
                    </div>

                    {/* Lean-Core Pass 2 · 2C · Missing evidence surfacing.
                        Reads unresolved_facts from the constitutional model — does NOT
                        interpret or override. Actions still route through the existing
                        Founder Decision modal (which preserves Law-Before-Math). */}
                    {Array.isArray(op.unresolved_facts) && op.unresolved_facts.length > 0 && (
                      <div className="border-t border-line pt-3 mt-2" data-testid={`missing-evidence-${op.id}`}>
                        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Missing evidence · unresolved facts</div>
                        <ul className="text-xs text-ink space-y-0.5 list-disc list-inside">
                          {op.unresolved_facts.map((f, i) => (
                            <li key={i}>{typeof f === "string" ? f : (f?.label || f?.field || JSON.stringify(f))}</li>
                          ))}
                        </ul>
                        <div className="text-[10px] text-ink-soft italic mt-1">
                          To resolve: open Founder Decision → "note" with a structured research mandate. Perplexity handoff is NOT YET CONNECTED.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
            </>
          );
        })()}

        <h2 className="font-serif text-2xl text-ink mb-3">Active Sources</h2>
        <ul className="space-y-2" data-testid="sources-list">
          {sources.map(s => (
            <li key={s.source_id} className="border border-line bg-admin-surface rounded-xl p-4 flex items-start gap-4">
              {s.source_kind === "rss" ? <Rss className="h-5 w-5 text-terracotta mt-1" strokeWidth={1.5} /> :
               s.source_kind === "html" ? <Globe className="h-5 w-5 text-terracotta mt-1" strokeWidth={1.5} /> :
               <CheckCircle2 className="h-5 w-5 text-forest mt-1" strokeWidth={1.5} />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-serif text-lg text-ink">{s.label}</div>
                  <Badge className="bg-bone text-ink border border-line text-[10px]">{s.source_kind}</Badge>
                  {s.is_fragile && (
                    <Badge className="bg-terracotta/20 text-terracotta border border-terracotta/40 text-[10px] uppercase tracking-widest">Fragile</Badge>
                  )}
                  <span className="text-xs text-ink-soft">every {s.cadence_hours}h</span>
                </div>
                <div className="text-xs text-ink-soft mt-1">
                  {s.last_success_at ? `Last poll: ${new Date(s.last_success_at).toLocaleString()} · seen ${s.items_seen}, new ${s.items_new}, auto-qualified ${s.items_qualified}` : "Not yet polled"}
                </div>
                {s.last_error && (
                  <div className="text-xs text-terracotta italic mt-1">Last error: {s.last_error}</div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => refreshOne(s.source_id)} data-testid={`refresh-${s.source_id}`} className="border-line">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Poll
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="font-serif text-2xl text-ink mb-3">Founder Watchlist</h2>
        <p className="text-xs text-ink-soft mb-3 max-w-2xl">
          Add foundation pages (HTML — fetched weekly) or RSS feeds (daily). HTML watchlist items are always marked <strong>Fragile — verify manually</strong> because foundation sites change layout without notice.
        </p>
        <div className="border border-line bg-admin-surface rounded-xl p-4 grid gap-3 md:grid-cols-5 mb-4" data-testid="add-watchlist">
          <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} data-testid="watchlist-kind" className="h-9 border border-line rounded-md bg-bone px-3 text-sm">
            <option value="html">HTML page (weekly)</option>
            <option value="rss">RSS feed (daily)</option>
          </select>
          <Input placeholder="Label (e.g. Ford Foundation)" value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="watchlist-label" />
          <Input placeholder="URL" value={form.url} onChange={e => setForm({...form, url: e.target.value})} className="md:col-span-2" data-testid="watchlist-url" />
          <div className="flex gap-2">
            <Input placeholder="Funder (optional)" value={form.funder} onChange={e => setForm({...form, funder: e.target.value})} data-testid="watchlist-funder" />
            <Button onClick={addWatch} data-testid="watchlist-add" className="bg-forest text-bone hover:bg-ink rounded-full shrink-0">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {watchlist.length === 0 ? (
          <div className="text-sm text-ink-soft italic text-center py-6 border border-dashed border-line rounded-xl">
            No watchlist entries yet. Recommended starters: Ford Foundation, MacArthur, Kellogg, Surdna, Mellon, Hewlett, Compton, Fetzer, Native Arts & Cultures, Cultural Survival, Christensen Fund, Swift Foundation.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="watchlist-list">
            {watchlist.map(w => (
              <li key={w.id} className="border border-line bg-admin-surface rounded-xl p-3 flex items-center gap-3">
                {w.kind === "rss" ? <Rss className="h-4 w-4 text-terracotta" strokeWidth={1.5} /> : <Globe className="h-4 w-4 text-terracotta" strokeWidth={1.5} />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink flex items-center gap-2">
                    {w.label}
                    <Badge className="bg-bone text-ink border border-line text-[10px]">{w.kind}</Badge>
                    {!w.is_active && <Badge className="bg-line text-ink-soft text-[10px]">inactive</Badge>}
                  </div>
                  <div className="text-[10px] text-ink-soft truncate">{w.url}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => removeWatch(w.id)} data-testid={`remove-${w.id}`} className="border-line">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <FounderDecisionDialog
        open={!!decisionOp}
        opportunity={decisionOp}
        onClose={() => setDecisionOp(null)}
        onSaved={load}
      />
    </div>
  );
}
